import React, { useState, useEffect } from 'react'
import { Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'

export default function ContactForm({ defaultService = '', title = 'Få et uforpligtende tilbud' }) {
  const getInitialEventType = (serviceKey) => {
    if (!serviceKey) return 'DJ til bryllup'
    const s = String(serviceKey).toLowerCase()
    if (s.includes('sølvbryllup') || s.includes('soelvbryllup')) return 'Sølvbryllup'
    if (s.includes('bryllup')) return 'DJ til bryllup'
    if (s.includes('firmafest') || s.includes('firma')) return 'DJ til firmafest'
    if (s.includes('foedselsdag') || s.includes('fødselsdag')) return 'DJ til fødselsdag'
    if (s.includes('saxofon') || s.includes('sax')) return 'DJ & Saxofon'
    if (s.includes('vinyl')) return 'Vinyl DJ'
    if (s.includes('andet')) return 'Andet'
    return 'DJ til bryllup'
  }

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    event_type: getInitialEventType(defaultService),
    date: '',
    message: '',
    website_url: '', // Honeypot
  })

  useEffect(() => {
    if (defaultService) {
      setFormData((prev) => ({
        ...prev,
        event_type: getInitialEventType(defaultService),
      }))
    }
  }, [defaultService])

  const [status, setStatus] = useState('idle') // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('')

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus('submitting')
    setErrorMessage('')

    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (response.ok) {
        setStatus('success')
        setFormData({
          name: '',
          email: '',
          phone: '',
          event_type: getInitialEventType(defaultService),
          date: '',
          message: '',
          website_url: '',
        })
      } else {
        const errData = await response.json().catch(() => ({}))
        throw new Error(errData.message || 'Der skete en fejl ved indsendelsen. Prøv igen.')
      }
    } catch (err) {
      console.error('Fejl ved formularindsendelse:', err)
      setStatus('error')
      setErrorMessage(err.message || 'Netværksfejl. Kontroller din internetforbindelse og prøv igen.')
    }
  }

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-950/80 p-6 shadow-backlight-hover md:shadow-backlight md:hover:shadow-backlight-hover transition-all duration-300 backdrop-blur-md md:p-10">
      <div className="mb-8 text-center">
        <h3 className="font-serif text-2xl font-bold text-white text-center">
          {title}
        </h3>
        <p className="mt-2 text-sm text-zinc-400">
          Udfyld formularen, så vender jeg tilbage inden for 12 timer
        </p>
      </div>

      {status === 'success' ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <CheckCircle className="h-16 w-16 text-emerald-500 animate-bounce" />
          <h4 className="mt-4 text-xl font-bold text-white">Tak for din forespørgsel!</h4>
          <p className="mt-2 text-sm text-zinc-400 max-w-md">
            Jeg har modtaget dine oplysninger og vil kigge på dem med det samme. Du hører fra mig inden for kort tid!
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="mt-6 rounded-full border border-[#bb9b56]/30 px-6 py-2 text-sm font-medium text-[#bb9b56] hover:bg-[#bb9b56]/10 transition-colors"
          >
            Send en ny forespørgsel
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Honeypot field - ABSOLUTELY HIDDEN from humans */}
          <div className="sr-only absolute -z-50 overflow-hidden h-0 w-0">
            <label htmlFor="website_url">Lad dette felt være tomt</label>
            <input
              id="website_url"
              type="text"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {/* Navn */}
            <div>
              <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-zinc-400">
                Navn *
              </label>
              <input
                id="name"
                type="text"
                name="name"
                required
                autoComplete="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Dit fulde navn"
                className="mt-1.5 w-full rounded-lg border border-zinc-800 bg-zinc-900/50 px-4 py-3 text-sm text-white placeholder-zinc-600 focus:border-[#bb9b56]/50 focus:outline-none focus:ring-1 focus:ring-[#bb9b56]/50"
              />
            </div>

            {/* E-mail */}
            <div>
              <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-zinc-400">
                E-mail *
              </label>
              <input
                id="email"
                type="email"
                name="email"
                required
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="din@email.dk"
                className="mt-1.5 w-full rounded-lg border border-zinc-800 bg-zinc-900/50 px-4 py-3 text-sm text-white placeholder-zinc-600 focus:border-amber-500/50 focus:outline-none focus:ring-1 focus:ring-amber-500/50"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {/* Telefon */}
            <div>
              <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider text-zinc-400">
                Telefonnummer *
              </label>
              <input
                id="phone"
                type="tel"
                name="phone"
                required
                autoComplete="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="F.eks. 50998282"
                className="mt-1.5 w-full rounded-lg border border-zinc-800 bg-zinc-900/50 px-4 py-3 text-sm text-white placeholder-zinc-600 focus:border-amber-500/50 focus:outline-none focus:ring-1 focus:ring-amber-500/50"
              />
            </div>

            {/* Dato */}
            <div>
              <label htmlFor="date" className="block text-xs font-semibold uppercase tracking-wider text-zinc-400">
                Dato for arrangement *
              </label>
              <input
                id="date"
                type="date"
                name="date"
                required
                value={formData.date}
                onChange={handleChange}
                className="mt-1.5 w-full rounded-lg border border-zinc-800 bg-zinc-900/50 px-4 py-3 text-sm text-white focus:border-[#bb9b56]/50 focus:outline-none focus:ring-1 focus:ring-[#bb9b56]/50"
              />
            </div>
          </div>

          {/* Type af arrangement */}
          <div>
            <label htmlFor="event_type" className="block text-xs font-semibold uppercase tracking-wider text-zinc-400">
              Type af arrangement
            </label>
            <select
              id="event_type"
              name="event_type"
              value={formData.event_type}
              onChange={handleChange}
              className="mt-1.5 w-full rounded-lg border border-zinc-800 bg-zinc-900/50 px-4 py-3 text-sm text-white focus:border-[#bb9b56]/50 focus:outline-none focus:ring-1 focus:ring-[#bb9b56]/50"
            >
              <option value="DJ til bryllup">DJ til bryllup</option>
              <option value="Sølvbryllup">Sølvbryllup</option>
              <option value="DJ til firmafest">DJ til firmafest</option>
              <option value="DJ til fødselsdag">DJ til fødselsdag</option>
              <option value="DJ & Saxofon">DJ & Saxofon</option>
              <option value="Vinyl DJ">Vinyl DJ</option>
              <option value="Andet">Andet</option>
            </select>
          </div>

          {/* Besked */}
          <div>
            <label htmlFor="message" className="block text-xs font-semibold uppercase tracking-wider text-zinc-400">
              Fortæl om jeres ønsker & festen
            </label>
            <textarea
              id="message"
              name="message"
              rows="4"
              value={formData.message}
              onChange={handleChange}
              placeholder="Skriv gerne lidt om lokation, forventet antal gæster, musikønsker osv..."
              className="mt-1.5 w-full rounded-lg border border-zinc-800 bg-zinc-900/50 px-4 py-3 text-sm text-white placeholder-zinc-600 focus:border-[#bb9b56]/50 focus:outline-none focus:ring-1 focus:ring-[#bb9b56]/50"
            />
          </div>

          {status === 'error' && (
            <div className="flex items-start gap-2 rounded-lg bg-red-500/10 p-3 text-sm text-red-400">
              <AlertCircle className="h-5 w-5 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'submitting'}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#bb9b56] py-3.5 text-sm font-bold text-white shadow-lg shadow-[#bb9b56]/15 transition-all hover:scale-[1.02] hover:bg-[#a88645] disabled:scale-100 disabled:opacity-50"
          >
            {status === 'submitting' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Sender forespørgsel...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Få et uforpligtende tilbud nu
              </>
            )}
          </button>
        </form>
      )}
    </div>
  )
}
